import React, { FC, memo, Fragment } from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import pdfStyles, { BASE_FONT_SIZE, PDF_PRIMARY_COLOR } from '../pdfStyles';
import { DETAILS_REPAY_PAYMENT_REQUISITION } from '../Vouchers/voucherHelpers';

const styles = StyleSheet.create({
    table: {
        borderRadius: 2,
        overflow: 'hidden'
    },
    tableRowHead: {
        flexDirection: 'row',
        borderBottom: `1.5px solid ${PDF_PRIMARY_COLOR}`,
        backgroundColor: '#f8f9fa'
    },
    tableRow: {
        borderBottom: '0.5px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'row'
    },
    tableCellHead: {
        fontSize: BASE_FONT_SIZE,
        fontWeight: 'bold',
        padding: '5 6',
        width: '100%',
        color: PDF_PRIMARY_COLOR
    },
    wrapTableCell: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: '4 0'
    },
    iconTableCell: {
        padding: 0,
        alignItems: 'center',
        justifyContent: 'center'
    },
    borderLeft: {},
    borderLeftHead: {},
    tableRowSummary: {
        borderTop: `1px solid ${PDF_PRIMARY_COLOR}`,
        fontWeight: 'bold'
    },
    multilineContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    }
});

export type PdfTablePropsRowArrayItem = { text: string; style?: { [key: string]: string | number } };

export type PdfTablePropsRow = {
    // eslint-disable-next-line no-undef
    [key: string]: string | number | Array<PdfTablePropsRowArrayItem> | JSX.Element;
};
export type PdfTablePropsColumn = {
    title?: string;
    fieldName: string;
    style?: { [key: string]: string };
    isIcon?: boolean;
};
export type PdfTableProps = {
    columns: Array<PdfTablePropsColumn>;
    rows: Array<PdfTablePropsRow>;
    summaryLine?: PdfTablePropsRow;
    summaryLineStyle?: object;
    headerLineStyle?: object;
    tableStyle?: object;
    voucherNumber?: number;
    travelFileNumber?: string;
    /**
     * When true, columns share the row width proportionally to their `minWidth`
     * (used as a flex-grow weight). Default false: each column gets equal share
     * (legacy behavior — minWidth acts only as a floor).
     */
    proportionalColumns?: boolean;
    /**
     * Row key whose value groups consecutive rows into one block that never
     * splits across a page break (e.g. the rooming list keeps a room's
     * passengers together). Rows with an empty value form a block of their own.
     */
    rowGroupField?: string;
};

const parseMinWidthAsFlex = (style?: { [key: string]: string }): number => {
    const v = style?.minWidth?.replace('px', '');
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 60;
};

const formatMultilineText = (text: string) => {
    if (typeof text !== 'string') return text;

    const lines = text.split(/\r?\n/);
    if (lines.length <= 1) return <Text style={{ padding: '0 6' }}>{text}</Text>;

    return (
        <View style={styles.multilineContainer}>
            {lines.map((line, index) => {
                if (line.includes('(HSS)')) {
                    const [hebrewPart] = line.split('(HSS)');
                    return (
                        <Text key={index} style={{ padding: '0 6', textAlign: 'right' }}>
                            {`${hebrewPart.trim()} (HSS)`}
                        </Text>
                    );
                }

                const isHebrew = /[\u0590-\u05FF]/.test(line);
                return (
                    <Text
                        key={index}
                        style={{ padding: '0 6', textAlign: isHebrew ? 'right' : 'left' }}
                    >
                        {line.trim()}
                    </Text>
                );
            })}
        </View>
    );
};

// Consecutive rows sharing a non-empty `field` value form one group.
const groupRows = (rows: Array<PdfTablePropsRow> | undefined, field: string) => {
    const groups: Array<Array<{ row: PdfTablePropsRow; rowIdx: number }>> = [];
    let lastKey: unknown;
    (rows || []).forEach((row, rowIdx) => {
        const key = row[field];
        const current = groups[groups.length - 1];
        if (current && key !== '' && key != null && key === lastKey) {
            current.push({ row, rowIdx });
        } else {
            groups.push([{ row, rowIdx }]);
        }
        lastKey = key;
    });
    return groups;
};

const PdfTable: FC<PdfTableProps> = (props: PdfTableProps) => {
    const {
        columns,
        rows,
        summaryLine,
        summaryLineStyle,
        headerLineStyle,
        tableStyle,
        voucherNumber,
        travelFileNumber,
        proportionalColumns,
        rowGroupField
    } = props;

    const getCellSizing = (column: PdfTablePropsColumn) => {
        if (!proportionalColumns) return null;
        // An icon column keeps the width it asks for: a proportional share
        // squeezed the Rooming List's room arrows into a sliver.
        if (column.isIcon) return { flexGrow: 0, flexShrink: 0 };
        return { width: 'auto', flexBasis: 0, flexGrow: parseMinWidthAsFlex(column.style) };
    };

    const hasColumnTitles = () => {
        if (!columns) return false;
        for (let column of columns) {
            if (column.title) return true;
        }
        return false;
    };

    const renderRow = (row: PdfTablePropsRow, rowIdx: number) => {
        return (
            <View key={rowIdx} style={styles.tableRow}>
                {columns?.map((column, colIdx) => {
                    let rowValue = row[column.fieldName];
                    if (rowValue === DETAILS_REPAY_PAYMENT_REQUISITION) {
                        rowValue = `Repay - Payment Requisition No. ${voucherNumber || ''}. ${
                            travelFileNumber ? 'For Travel File #' + travelFileNumber : ''
                        }`;
                    }
                    let rowElement = <></>;
                    if (column.isIcon) {
                        rowElement = <Fragment key={`${rowIdx}-${colIdx}`}>{rowValue}</Fragment>;
                    } else {
                        if (typeof rowValue === 'number') {
                            rowElement = (
                                <Text style={{ padding: '0 6' }}>
                                    {taytanFormatNumberToString(rowValue || 0)}
                                </Text>
                            );
                        }
                        if (typeof rowValue === 'string') {
                            rowElement = formatMultilineText(rowValue);
                        }
                        if (Array.isArray(rowValue)) {
                            rowElement = (
                                <>
                                    {rowValue.map((cell, idx) => {
                                        return (
                                            <Text
                                                key={`${rowIdx}-${colIdx}-${idx}`}
                                                style={[
                                                    { padding: '0 6', width: '100%' },
                                                    cell.style || {}
                                                ]}
                                            >
                                                {cell.text}
                                            </Text>
                                        );
                                    })}
                                </>
                            );
                        }
                    }

                    return (
                        <View
                            key={`${rowIdx}-${colIdx}`}
                            style={[
                                styles.wrapTableCell,
                                colIdx ? styles.borderLeft : {},
                                column.style || {},
                                getCellSizing(column) || {},
                                // Centred, and no vertical padding, so a room's
                                // connector lines run on into the next row.
                                column.isIcon ? styles.iconTableCell : {}
                            ]}
                        >
                            {rowElement}
                        </View>
                    );
                })}
            </View>
        );
    };

    return (
        <View style={[styles.table, tableStyle || {}]}>
            {hasColumnTitles() && (
                <View style={styles.tableRowHead} fixed>
                    {columns?.map((column, colIdx) => (
                        <Text
                            key={`head-${colIdx}`}
                            style={[
                                styles.tableCellHead,
                                colIdx ? styles.borderLeftHead : {},
                                column.style || {},
                                headerLineStyle || {},
                                getCellSizing(column) || {}
                            ]}
                        >
                            {column.title}
                        </Text>
                    ))}
                </View>
            )}
            {rowGroupField
                ? groupRows(rows, rowGroupField).map((group) => (
                      <View key={`group-${group[0].rowIdx}`} wrap={false}>
                          {group.map(({ row, rowIdx }) => renderRow(row, rowIdx))}
                      </View>
                  ))
                : rows?.map((row, rowIdx) => renderRow(row, rowIdx))}
            {summaryLine ? (
                <View style={[styles.tableRow, styles.tableRowSummary]}>
                    {columns.map((column, colIdx) => {
                        const summaryValue = summaryLine[column.fieldName];

                        if (Array.isArray(summaryValue)) {
                            return (
                                <View
                                    key={`summary-${colIdx}`}
                                    style={[
                                        { padding: '0 6', width: '100%' },
                                        colIdx ? styles.borderLeft : {},
                                        summaryLineStyle || {},
                                        column.style || {},
                                        getCellSizing(column) || {}
                                    ]}
                                >
                                    {summaryValue.map((cell, idx) => {
                                        return (
                                            <Text key={`summary-${colIdx}-${idx}`} style={[cell.style || {}]}>
                                                {cell.text}
                                            </Text>
                                        );
                                    })}
                                </View>
                            );
                        }

                        if (typeof summaryValue === 'number') {
                            return (
                                <Text
                                    key={`summary-${colIdx}`}
                                    style={[
                                        { padding: '0 6', width: '100%' },
                                        colIdx ? styles.borderLeft : {},
                                        column.style || {},
                                        summaryLineStyle || {},
                                        getCellSizing(column) || {}
                                    ]}
                                >
                                    {taytanFormatNumberToString(summaryValue || 0)}
                                </Text>
                            );
                        }

                        return (
                            <Text
                                key={`summary-${colIdx}`}
                                style={[
                                    { padding: '0 6', width: '100%' },
                                    colIdx ? styles.borderLeft : {},
                                    column.style || {},
                                    summaryLineStyle || {},
                                    getCellSizing(column) || {}
                                ]}
                            >
                                {summaryValue}
                            </Text>
                        );
                    })}
                </View>
            ) : (
                <></>
            )}
        </View>
    );
};

export default memo(PdfTable);
